import { useQuery } from "@apollo/client";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { configArrayNew } from "components/Environment/configArray";
import EnvironmentContextProvider from "components/Environment/EnvironmentContext";
import EnvironmentPage from "components/Environment/view/EnvironmentPage";
import NotFoundPage from "components/error/NotFoundPage";
import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Module } from "types/generated";

import { ModuleContext } from "../Context";
import { GET_MODULE_ENVIRONMENT } from "./gql";

type GetModuleEnvironmentGql = {
  module: Module;
};

const EnvironmentNew = () => {
  const { onError } = useTypedContext(FlashContext);
  const { module: moduleCtx, hasModuleWriteAccess } = useTypedContext(ModuleContext);

  const { error, loading, data } = useQuery<GetModuleEnvironmentGql>(GET_MODULE_ENVIRONMENT, {
    variables: { moduleId: moduleCtx.id },
    onError,
  });

  useTitle(`Environment · ${moduleCtx.name}`);

  useBreadcrumbs([
    {
      title: "Modules",
      link: "/modules",
    },
    {
      title: moduleCtx.id,
    },
  ]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.module) {
    return <PageLoading />;
  }

  if (!data?.module) {
    return <NotFoundPage />;
  }

  const module = { ...moduleCtx, ...data.module };
  const [localVariables, spaceliftVariables, mountedFiles] = configArrayNew(module.runtimeConfig);
  return (
    <EnvironmentContextProvider
      resourceId={module.id}
      refetchQueries={[GET_MODULE_ENVIRONMENT]}
      canCreateAndEditVariables={hasModuleWriteAccess}
    >
      <EnvironmentPage
        localVariables={localVariables}
        mountedFiles={mountedFiles}
        spaceliftVariables={spaceliftVariables}
        attachedContexts={module.attachedContexts}
      />
    </EnvironmentContextProvider>
  );
};

export default EnvironmentNew;
